import type { useClerk } from '@clerk/nextjs';
import { AUTH_ENDPOINTS, AUTH_ROUTES } from '@lib/constants/apis';
import { getLogger } from '@lib/observability/logging/log.client';

const { logInfo, logError } = getLogger(
  'lib/clerk/utils/reset-session-and-reload'
);

// Cap the FAPI sign-out so a hung or broken Clerk backend can't block
// recovery; the cookie-route wipe + reload still run after the cap.
const SIGN_OUT_TIMEOUT_MS = 2500;

function timeout(ms: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, ms));
}

/**
 * Full auth reset for a stuck Clerk client. signOut ends the session
 * at the Clerk backend; the server route then expires every Clerk
 * cookie on host + apex domain (reaching the cross-instance collision
 * markers signOut leaves behind), client storage is dropped, and a
 * hard reload re-bootstraps a clean /login. signOut is load-bearing:
 * expiring cookies alone leaves the backend session alive, so a reload
 * would re-derive the same stuck state.
 */
export async function resetSessionAndReload(
  clerk: ReturnType<typeof useClerk>
): Promise<void> {
  // Log info so we can troubleshoot the dead Google button recovery.
  logInfo('Auth reset: signing out, clearing cookies + storage, reloading');

  // End the session server-side. The `() => {}` callback suppresses
  // Clerk's own post-signout redirect so this controls the reload.
  // Docs:
  //   https://clerk.com/docs/references/javascript/clerk#sign-out
  if (clerk.loaded) {
    try {
      await Promise.race([
        clerk.signOut(() => {}),
        timeout(SIGN_OUT_TIMEOUT_MS),
      ]);
    } catch (err) {
      // Log error so we can investigate a failed Clerk signOut; the
      // cookie wipe + reload below still recover most stuck state.
      logError('Auth reset: clerk signOut failed', err as Error);
    }
  }

  try {
    await fetch(AUTH_ENDPOINTS.RESET_SESSION, {
      method: 'POST',
      cache: 'no-store',
    });
  } catch (err) {
    // Log error so we can investigate a failed cookie reset; the
    // reload still runs and recovers most of the stuck state.
    logError('Auth reset: cookie route failed', err as Error);
  }

  try {
    window.localStorage.clear();
  } catch {
    // Storage access throws under some privacy settings; the cookie
    // expiry above is the load-bearing part, so ignore and reload.
  }

  // Hard navigation so Clerk re-bootstraps from zero state.
  window.location.assign(AUTH_ROUTES.LOGIN);
}
